import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  RadioGroup,
  Tab,
  Tabs,
} from "@mui/material";
import {
  brand,
  Button,
  Icon,
  Radio,
  Typography,
} from "@platform/design-system";
import { useState } from "react";

import { EnrollmentTable } from "./EnrollmentTable";

import type { FC } from "react";
import type { EnrollmentDecision } from "../../types";
import type { EnrollmentReview } from "../../utils/quotedEnrollmentReview";

/** The Figma frame is about 700px wide; wider only stretches the two tables apart. */
const DIALOG_MAX_WIDTH = 720;

const CLEARS_FLAG_NOTE = (
  <Typography variant="body-large" component="span" color="textSecondary">
    {" (Clears flag)"}
  </Typography>
);

export interface ReviewQuotedEnrollmentDialogProps {
  /** The review to show. The dialog is open while this is defined. */
  review: EnrollmentReview | undefined;
  carrierName: string;
  isApplying: boolean;
  onApply: (decision: EnrollmentDecision) => void;
  onCancel: () => void;
}

const isEnrollmentDecision = (value: string): value is EnrollmentDecision =>
  value === "kept_current" || value === "used_carrier";

const enrolledMeasureOf = (review: EnrollmentReview): string =>
  review.totalDifferenceMeasure === "volume" ? "Volume" : "Employees";

const DifferenceSummary: FC<{ review: EnrollmentReview }> = ({ review }) => {
  const measure = enrolledMeasureOf(review);

  if (review.totalDifferencePercent === undefined) {
    return (
      <>{`Carrier enrollment compared with Current's, by Enrolled ${measure}`}</>
    );
  }

  return (
    <>
      <Box
        component="strong"
        sx={{ color: review.flagged ? "warning.main" : "success.main" }}
      >
        {review.totalDifferencePercent}%
      </Box>
      {` difference in total Enrolled ${measure}`}
    </>
  );
};

interface ReviewFormProps extends ReviewQuotedEnrollmentDialogProps {
  review: EnrollmentReview;
}

/**
 * The dialog's body, mounted only while there is a review so each review starts from the
 * choice already on record and the first option's tab.
 */
const ReviewForm: FC<ReviewFormProps> = ({
  review,
  carrierName,
  isApplying,
  onApply,
  onCancel,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [choice, setChoice] = useState<EnrollmentDecision | undefined>(
    review.decision,
  );

  const option = review.options[selectedTab] ?? review.options[0];
  const hasTabs = review.options.length > 1;
  const optionSuffix = hasTabs && option ? ` (${option.label} Option)` : "";
  const canApply =
    choice !== undefined && choice !== review.decision && !isApplying;

  return (
    <>
      <DialogTitle>Review Quoted Enrollment</DialogTitle>
      <DialogContent>
        <Box
          data-testid="enrollment-difference-summary"
          data-flagged={String(review.flagged)}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          <Icon
            name={review.flagged ? "warning" : "success"}
            color={review.flagged ? "warning" : "success"}
          />
          <Typography variant="body-medium" component="span">
            <DifferenceSummary review={review} />
          </Typography>
        </Box>
        <Typography variant="body-medium" sx={{ mb: 2 }}>
          {review.flagged ? (
            <>
              <strong>{carrierName}&apos;s</strong> proposal doesn&apos;t match
              current enrollment. Select to either continue using the{" "}
              <strong>Current Enrollment</strong> numbers or replace with{" "}
              <strong>{carrierName}</strong> proposal numbers.
            </>
          ) : (
            <>
              <strong>{carrierName}&apos;s</strong> proposal is within tolerance
              of current enrollment. You can still select to either continue
              using the <strong>Current Enrollment</strong> numbers or replace
              with <strong>{carrierName}</strong> proposal numbers.
            </>
          )}
        </Typography>

        {hasTabs && (
          <Tabs
            value={selectedTab}
            onChange={(_event, value: number) => setSelectedTab(value)}
            sx={{ mb: 2 }}
          >
            {review.options.map((tabOption) => (
              <Tab
                key={tabOption.planId}
                label={tabOption.label}
                icon={
                  tabOption.flagged ? (
                    <Icon name="warning" color="error" />
                  ) : undefined
                }
                iconPosition="start"
              />
            ))}
          </Tabs>
        )}

        {option && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              mb: 2,
              "& > * + *": {
                pl: 3,
                borderLeft: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <EnrollmentTable
              side="current"
              title={`Current Enrollment${optionSuffix}`}
              rows={option.rows}
            />
            <EnrollmentTable
              side="carrier"
              title={`${carrierName}${optionSuffix}`}
              rows={option.rows}
            />
          </Box>
        )}

        <Typography variant="header-medium" component="h3" sx={{ mb: 0.5 }}>
          Select an Option
        </Typography>
        <RadioGroup
          // The base Radio takes the app theme's primary, which is not the brand purple
          // the Apply button uses; the selected dot should match that button.
          sx={{ "& .MuiRadio-root.Mui-checked": { color: brand("primary") } }}
          value={choice ?? ""}
          onChange={(event) => {
            if (isEnrollmentDecision(event.target.value)) {
              setChoice(event.target.value);
            }
          }}
        >
          <FormControlLabel
            value="kept_current"
            control={<Radio />}
            disabled={isApplying}
            label={
              <>
                Keep Current Enrollment for all options
                {review.flagged && CLEARS_FLAG_NOTE}
              </>
            }
          />
          <FormControlLabel
            value="used_carrier"
            control={<Radio />}
            disabled={isApplying}
            label={
              <>
                Accept {carrierName} Enrollment for all options
                {review.flagged && CLEARS_FLAG_NOTE}
              </>
            }
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isApplying} variant="tertiary">
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (choice !== undefined) onApply(choice);
          }}
          disabled={!canApply}
          variant="primary"
        >
          {isApplying ? "Applying…" : "Apply"}
        </Button>
      </DialogActions>
    </>
  );
};

/**
 * Compares a quoted plan group's carrier enrollment with Current's, side by side, and lets
 * the analyst keep Current's or accept the carrier's for every option of the group.
 */
export const ReviewQuotedEnrollmentDialog: FC<
  ReviewQuotedEnrollmentDialogProps
> = (props) => {
  const { review, isApplying, onCancel } = props;

  return (
    <Dialog
      open={review !== undefined}
      onClose={() => {
        if (!isApplying) onCancel();
      }}
      maxWidth={false}
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxWidth: DIALOG_MAX_WIDTH } }}
      data-testid="review-quoted-enrollment-dialog"
    >
      {review && <ReviewForm key={review.groupId} {...props} review={review} />}
    </Dialog>
  );
};
